import { useSyncExternalStore } from 'react';
import type { PublicItem } from './types';

/**
 * Visitor "saved places" — a per-browser convenience stored in localStorage
 * (no account needed). Stores a minimal card snapshot so /saved works offline-first.
 */
const KEY = 'ylm-saved';
type Saved = Pick<PublicItem, '_id' | 'kind' | 'slug' | 'name' | 'title' | 'cover' | 'poster' | 'logo' | 'category' | 'village' | 'deity' | 'isSample'>;

let cache: Saved[] | null = null;
const listeners = new Set<() => void>();

function read(): Saved[] {
  if (cache) return cache;
  try {
    cache = JSON.parse(localStorage.getItem(KEY) ?? '[]');
  } catch {
    cache = [];
  }
  return cache!;
}

function write(items: Saved[]) {
  cache = items;
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* storage unavailable: keep in memory */
  }
  listeners.forEach((l) => l());
}

export function toggleSaved(item: PublicItem) {
  const items = read();
  if (items.some((i) => i._id === item._id)) write(items.filter((i) => i._id !== item._id));
  else write([{ _id: item._id, kind: item.kind, slug: item.slug, name: item.name, title: item.title, cover: item.cover, poster: item.poster, logo: item.logo, category: item.category, village: item.village, deity: item.deity, isSample: item.isSample }, ...items].slice(0, 200));
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  const onStorage = (e: StorageEvent) => { if (e.key === KEY) { cache = null; l(); } };
  window.addEventListener('storage', onStorage);
  return () => { listeners.delete(l); window.removeEventListener('storage', onStorage); };
};

export function useSaved() {
  return useSyncExternalStore(subscribe, read, () => [] as Saved[]);
}

export function useIsSaved(id: string) {
  return useSaved().some((i) => i._id === id);
}
